// The tasks tab: solve an exercise (the project's marked task file, or a chosen
// source) and inject the worked solution as a report section. Async — polls the
// solve status, then the report refetch surfaces the new section.
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  getReportSolveStatus,
  solveReportExercise,
  type ReportFileSurvey,
} from "@/api/report";
import { Alert, Button, Spinner } from "@/components/ui";

interface Props {
  projectId: string;
  files: ReportFileSurvey[];   // the report's source files (for selection)
  onSolved: () => void;        // refetch report (new section + plots)
}

export function TasksPanel({ projectId, files, onSolved }: Props) {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string>("");
  const [running, setRunning] = useState(false);

  const { data: status } = useQuery({
    queryKey: ["report-solve", projectId],
    queryFn: () => getReportSolveStatus(projectId),
    enabled: running,
    refetchInterval: (q) => {
      const s = q.state.data?.solve_status;
      return s === "running" ? 2500 : false;
    },
  });

  useEffect(() => {
    if (!status) return;
    if (status.solve_status === "complete") {
      setRunning(false);
      onSolved();
    } else if (status.solve_status === "failed") {
      setRunning(false);
      setError(status.solve_error || t("The solver failed."));
    }
  }, [status, onSolved, t]);

  const solve = async () => {
    setError(null);
    setRunning(true);
    try {
      await solveReportExercise(projectId, selected ? { exercise_file_id: selected } : {});
    } catch (e) {
      setRunning(false);
      setError(e instanceof Error ? e.message : t("Could not start the solver."));
    }
  };

  return (
    <div className="flex h-full flex-col gap-3 overflow-auto p-3">
      <p className="text-sm text-ink-soft">
        {t("Solve an exercise and insert the worked solution (derivation + plots) as a section.")}
      </p>
      <label className="text-xs font-medium text-ink-soft">{t("Exercise file")}</label>
      <select
        value={selected}
        onChange={(e) => setSelected(e.target.value)}
        className="rounded-lg border border-black/10 px-2 py-1 text-sm"
      >
        <option value="">{t("The file marked as the task")}</option>
        {files.map((f) => (
          <option key={f.id} value={f.id}>
            {f.filename}
          </option>
        ))}
      </select>
      <Button size="sm" disabled={running} onClick={solve}>
        {running ? (
          <span className="flex items-center gap-1">
            <Spinner className="h-3 w-3" /> {t("Solving…")}
          </span>
        ) : (
          t("Solve & insert")
        )}
      </Button>
      {running && <p className="text-xs text-ink-faint">{t("This runs in the sandbox and can take a minute.")}</p>}
      {error && <Alert>{error}</Alert>}
    </div>
  );
}
